import { Injectable } from '@angular/core';
import * as moment from 'moment-timezone';

import { IAzavistaGroupedFieldsComponentData, IField, IObjectEnumInputFieldValue, IIdWithLabel, FieldComponentType, IImageFieldOptions } from '@azavista/components/shared';
import { IUser } from '@azavista/servicelib';
import { SharedService, TRANSLATION_MAP, TranslationKey } from '../shared';
import { DateFormatPipe } from '../shared/pipes/date-format.pipe';
import { FieldsContainerComponent } from '../event/content/workflow-automations/details/fields-container.component';

export const PROFILE_IMAGE_OPTIONS: IImageFieldOptions = {
    cropping_mandatory: true,
    aspect_ratio: 1,
    maxHeight: 0,
    maxWidth: 0,
    roundCropper: true
};

export const getProfileImageField = (label: string) => ({
    id: 'profile_image_id',
    name: 'profile_image_id',
    label: label,
    category: '',
    schema: {
        type: [
            'string',
            'null'
        ],
        pattern: '^[a-f0-9]{24}$'
    },
    editable: true,
    visible_for_planners: true,
    default_field: false,
    type: 'image',
    builtin: true,
    options: PROFILE_IMAGE_OPTIONS
}) as IField;

@Injectable({
    providedIn: 'root'
})
export class MyAccountService {

    moment = moment;
    languages = this.sharedSvc.getAppSupportedLanguages();
    defaultLanguage: string;

    constructor(private sharedSvc: SharedService, private dateFormat: DateFormatPipe) {
        this.defaultLanguage = this.sharedSvc.getDefaultLanguage();
    }

    createNotificationsGroupedFieldsData(): IAzavistaGroupedFieldsComponentData {
        const fields: IField[] = [];
        const fieldMap = new Map<string, string>();
        // TODO: Use real field names once the back-end is ready
        fieldMap.set('field_name_1', TRANSLATION_MAP.SHOW_NOTIFICATIONS_WHEN_A_COMMENT_HAS_BEEN_MENTIONED);
        fieldMap.set('field_name_2', TRANSLATION_MAP.SHOW_NOTIFICATIONS_WHEN_A_TASK_HAS_BEEN_ASSIGNED);
        fieldMap.set('field_name_3', TRANSLATION_MAP.SHOW_NOTIFICATIONS_WHEN_A_TASK_HAS_BEEN_COMPLETED);
        fieldMap.set('field_name_4', TRANSLATION_MAP.SEND_EMAIL_WHEN_A_COMMENT_HAS_BEEN_MENTIONED);
        fieldMap.set('field_name_5', TRANSLATION_MAP.SEND_EMAIL_WHEN_A_TASK_HAS_BEEN_ASSIGNED);
        fieldMap.set('field_name_6', TRANSLATION_MAP.SEND_EMAIL_WHEN_A_TASK_HAS_BEEN_COMPLETED);
        for (const [fieldName, translationKey] of fieldMap) {
            fields.push({
                id: fieldName, name: fieldName, label: this.sharedSvc.translate(translationKey), editable: true,
                schema: { type: 'boolean' },
            } as IField);
        }
        const groupedFieldsData: IAzavistaGroupedFieldsComponentData = {
            editModeForAllFields: true,
            expandPanels: true,
            fieldsWithEntity: {
                entity: {},
                fields: fields,
            },
            singleColumn: true,
            disableAutoOrdering: true,
            // showActionButtons: false,
        };
        return groupedFieldsData;
    }

    createField(name: string, label: string, schemaType: string, component?: FieldComponentType): IField {
        const field = {
            id: name, name: name, category: '', editable: true, label: label,
            schema: { type: schemaType }, builtin: true, visible_for_planners: true
        } as IField;
        if (name === 'first_name' || name === 'last_name' || name === 'email') {
            field.mandatory_for_planners = true;
        }
        if (component) {
            field.component = component;
        }
        return field;
    }

    getUserFieldsData(user: IUser): IAzavistaGroupedFieldsComponentData {
        const timezones = this.moment.tz.names();
        const fields = this.getUserFields(timezones);
        const datetimeFormats = this.sharedSvc.getAppSupportedDateTimeFormats();
        const dateFormats = this.sharedSvc.getAppSupportedDateFormats();
        const timeFormats = this.sharedSvc.getAppSupportedTimeFormats();

        const userCopy = { ...user };
        userCopy.datetime_format = this.sharedSvc.createObjectEnumWithDateFormatEntityValue(datetimeFormats, user, u => u.datetime_format) as any;
        userCopy.date_format = this.sharedSvc.createObjectEnumWithDateFormatEntityValue(dateFormats, user, u => u.date_format) as any;
        userCopy.time_format = this.sharedSvc.createObjectEnumWithDateFormatEntityValue(timeFormats, user, u => u.time_format) as any;

        const languageItems = this.sharedSvc.getAppSupportedLanguages();
        userCopy.language = {
            displayProperty: 'label', reportProperty: 'id',
            items: languageItems,
            selected: languageItems.find(x => x.id === user.language)
        } as IObjectEnumInputFieldValue as any;

        const result: IAzavistaGroupedFieldsComponentData = {
            fieldsWithEntity: {
                fields: fields,
                entity: userCopy
            },
            expandPanels: true,
            editModeForAllFields: false,
            singleColumn: false,
            disableAutoOrdering: true
        };
        return result;
    }

    getUserFields(timezones: string[]): IField[] {
        const fields: IField[] = [];
        fields.push(this.createField('title', this.sharedSvc.translate(TranslationKey.title), 'string'));
        fields.push(this.createField('first_name', this.sharedSvc.translate(TranslationKey.firstName), 'string'));
        fields.push(this.createField('last_name', this.sharedSvc.translate(TranslationKey.lastName), 'string'));
        fields.push(this.createField('job_title', this.sharedSvc.translate(TranslationKey.function), 'string'));

        const emailField = this.createField('email', this.sharedSvc.translate(TranslationKey.email), 'string');
        emailField.schema.format = 'email';
        fields.push(emailField);

        fields.push(this.createField('phone', this.sharedSvc.translate(TranslationKey.phone), 'string'));

        const timezoneField = this.createField('timezone', this.sharedSvc.translate(TranslationKey.timeZone), 'string');
        timezoneField.schema.enum = timezones;
        fields.push(timezoneField);

        fields.push(this.createField(
            'language',
            this.sharedSvc.translate(TranslationKey.interfaceLanguage),
            'object', FieldComponentType.objectEnum)
        );
        fields.push(this.createField(
            'datetime_format', this.sharedSvc.translate(TranslationKey.datetimeFormat), 'object', FieldComponentType.objectEnum));
        fields.push(this.createField(
            'date_format',
            this.sharedSvc.translate(TranslationKey.dateFormat),
            'object', FieldComponentType.objectEnum)
        );
        fields.push(this.createField(
            'time_format',
            this.sharedSvc.translate(TranslationKey.timeFormat),
            'object', FieldComponentType.objectEnum)
        );

        return fields;
    }
}
